import {useEffect, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {ClipboardCheck, RefreshCw, ShieldX, WifiOff} from "lucide-react";
import {expireAuthSession} from "../../shared/api/client";
import {listApplications, mapOnboardingError, onboardingKeys} from "./api";
import {
    DEFAULT_FILTERS,
    useBusinessOnboardingWorkspace,
} from "./BusinessOnboardingWorkspace";
import {ApplicationQueue} from "./components/ApplicationQueue";
import {QueueFilters} from "./components/QueueFilters";
import {ServerPagination} from "./components/ServerPagination";
import "./businessOnboarding.css";
import {useTranslation} from "../../shared/i18n";


const hasActiveFilters = (filters) =>
    Object.keys(DEFAULT_FILTERS).some((key) => filters[key] !== DEFAULT_FILTERS[key]);

const QueueState = ({icon: Icon, title, copy, requestId, action}) => (
    <section className="onboarding-queue-state" aria-labelledby="onboarding-queue-state-title">
        <span className="onboarding-state-icon" aria-hidden="true">
            <Icon size={22} />
        </span>
        <div>
            <h2 id="onboarding-queue-state-title">{title}</h2>
            <p>{copy}</p>
            {requestId ? <p className="onboarding-request-id">Request ID: {requestId}</p> : null}
        </div>
        {action}
    </section>
);

export const ApplicationQueuePage = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {
        draftFilters,
        setDraftFilters,
        appliedFilters,
        applyFilters,
        resetFilters,
        filterError,
        page,
        setPage,
        countryScope,
    } = useBusinessOnboardingWorkspace();

    const applicationsQuery = useQuery({
        queryKey: onboardingKeys.applications(countryScope, appliedFilters, page),
        queryFn: () => listApplications({countryScope, filters: appliedFilters, page}),
        placeholderData: (previous, previousQuery) =>
            previousQuery?.queryKey?.[2] === String(countryScope) ? previous : undefined,
        enabled: Boolean(countryScope),
        refetchInterval: false,
        retry: false,
    });

    const mappedError = useMemo(
        () => (applicationsQuery.isError ? mapOnboardingError(applicationsQuery.error) : null),
        [applicationsQuery.error, applicationsQuery.isError],
    );

    useEffect(() => {
        if (mappedError?.kind !== "unauthorized") return;
        expireAuthSession();
    }, [mappedError?.kind]);

    useEffect(() => {
        if (mappedError?.kind !== "forbidden") return;
        queryClient.removeQueries({queryKey: onboardingKeys.all});
    }, [mappedError?.kind, queryClient]);

    const data = applicationsQuery.data;
    const refreshing = applicationsQuery.isFetching && Boolean(data);

    const errorState = (() => {
        if (!mappedError) return null;
        if (mappedError.kind === "unauthorized") {
            return (
                <QueueState
                    icon={WifiOff}
                    title={t("sections.onboarding.p17")}
                    copy={mappedError.message}
                    requestId={mappedError.requestId}
                    action={
                        <a className="admin-button admin-button-primary" href="/">
                            {t("sections.onboarding.p29")}
                        </a>
                    }
                />
            );
        }
        if (mappedError.kind === "forbidden") {
            return (
                <QueueState
                    icon={ShieldX}
                    title={t("sections.onboarding.p30")}
                    copy={mappedError.message}
                    requestId={mappedError.requestId}
                />
            );
        }
        return (
            <QueueState
                icon={WifiOff}
                title={
                    mappedError.kind === "contract"
                        ? t("sections.onboarding.p18")
                        : t("sections.onboarding.p31")
                }
                copy={mappedError.message}
                requestId={mappedError.requestId}
                action={
                    <button
                        className="admin-button admin-button-secondary"
                        type="button"
                        onClick={() => applicationsQuery.refetch()}
                    >
                        <RefreshCw aria-hidden="true" size={16} />
                        {t("sections.onboarding.p4")}
                    </button>
                }
            />
        );
    })();

    return (
        <div className="admin-page onboarding-page">
            <header className="admin-page-header onboarding-page-header">
                <div>
                    <h1 className="admin-page-title">{t("sections.onboarding.p32")}</h1>
                    <p className="admin-page-copy">
                        {t("sections.onboarding.p33")}
                    </p>
                </div>
                <div className="admin-pill onboarding-count-pill">
                    <ClipboardCheck aria-hidden="true" size={15} />
                    {data ? t("sections.onboarding.p34", {v0: data.count}) : t("sections.onboarding.p35")}
                </div>
            </header>

            <QueueFilters
                draftFilters={draftFilters}
                setDraftFilters={setDraftFilters}
                onApply={applyFilters}
                onReset={resetFilters}
                error={filterError}
                busy={refreshing}
            />

            <div className="onboarding-progress-slot">
                {refreshing ? (
                    <div
                        className="onboarding-progress"
                        role="progressbar"
                        aria-label={t("sections.onboarding.p36")}
                    />
                ) : null}
            </div>

            {errorState}

            {!errorState ? (
                <section className="onboarding-queue-panel" aria-label={t("sections.onboarding.p37")}>
                    <ApplicationQueue
                        applications={data?.results || []}
                        loading={applicationsQuery.isLoading}
                        refreshing={refreshing}
                    />

                    {!applicationsQuery.isLoading && data?.results.length === 0 ? (
                        <QueueState
                            icon={ClipboardCheck}
                            title={
                                hasActiveFilters(appliedFilters)
                                    ? t("sections.onboarding.p38")
                                    : t("sections.onboarding.p39")
                            }
                            copy={
                                hasActiveFilters(appliedFilters)
                                    ? t("sections.onboarding.p40")
                                    : t("sections.onboarding.p41")
                            }
                        />
                    ) : null}

                    {!applicationsQuery.isLoading && data ? (
                        <ServerPagination
                            page={page}
                            count={data.count}
                            next={data.next}
                            previous={data.previous}
                            busy={applicationsQuery.isFetching}
                            onPageChange={setPage}
                        />
                    ) : null}
                </section>
            ) : null}
        </div>
    );
};
